// app/frontend/components/Notifications/NotificationDropdown.tsx
import React from 'react';
import { ScrollArea, ActionIcon, Divider, Group, Stack, Paper, Box, Center, UnstyledButton } from '@mantine/core';
import { ThemedText } from '../Typography';
import { IconX, IconBell } from '@tabler/icons-react';
import { Notification } from '../../hooks/useNotifications';
import { COLORS } from '../../src/constants/theme';
import { CountBadge } from '../ui';
import { useIsMobile } from '../../src/context/MediaQueryContext';
import { themeAlpha, themeVar } from '../../src/utils/pageTheme';
import { getNotificationIcon } from './notificationIconMap';

interface NotificationDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  onMarkAsRead: (id: number) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: number) => void;
  onClose: () => void;
}

// The list hugs its content and never pushes the panel (navbar above, header +
// footer around the list) off screen (#2400). Both run toward the bottom of
// the screen; desktop stops at 720px so big monitors don't get a giant column.
// dvh follows mobile browser toolbars.
const VIEWPORT_FIT = 'calc(100dvh - 200px)';
const LIST_MAX_HEIGHT = `min(720px, ${VIEWPORT_FIT})`;

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onClose,
}) => {
  const isMobile = useIsMobile();

  return (
    <Paper
      shadow="xl"
      radius="md"
      withBorder
      style={{
        width: '400px',
        maxWidth: '90vw',
        overflow: 'hidden',
      }}
      styles={{
        root: {
          backgroundColor: '#171717',
          borderColor: '#333',
          color: COLORS.textPrimary,
        },
      }}
    >
      {/* Header */}
      <Box p="sm" style={{ borderBottom: '1px solid #333' }}>
        <Group justify="space-between" align="center" wrap="nowrap">
          <ThemedText fw={600} size="md">
            Notifications
          </ThemedText>
          <Group gap={4} wrap="nowrap" align="center">
            {unreadCount > 0 && (
              <UnstyledButton
                onClick={onMarkAllAsRead}
                style={{
                  fontSize: 12,
                  color: themeVar('accent-text'),
                  fontWeight: 500,
                  padding: '4px 8px',
                  borderRadius: 4,
                }}
              >
                Mark all as read
              </UnstyledButton>
            )}
            <ActionIcon variant="subtle" color="gray" onClick={onClose} size="sm">
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>
      </Box>

      {/* Notifications List */}
      <ScrollArea.Autosize mah={isMobile ? VIEWPORT_FIT : LIST_MAX_HEIGHT}>
        {!notifications || notifications.length === 0 ? (
          <Center p="xl">
            <Stack align="center" gap="sm">
              <IconBell size={40} color={COLORS.textDimmed} strokeWidth={1.5} />
              <ThemedText size="sm" variant="dimmed">
                No notifications yet
              </ThemedText>
              <ThemedText size="xs" style={{ color: COLORS.textSubtle }}>
                We'll notify you when something happens.
              </ThemedText>
            </Stack>
          </Center>
        ) : (
          notifications.map((notification) => (
            <Box key={notification.id}>
              <Box
                p="sm"
                style={{
                  backgroundColor: notification.read ? '#171717' : '#1f1f22',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#26262a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = notification.read ? '#171717' : '#1f1f22';
                }}
                onClick={() => {
                  if (!notification.read) {
                    onMarkAsRead(notification.id);
                  }
                  if (notification.action_url) {
                    window.location.href = notification.action_url;
                  }
                }}
              >
                <Group align="flex-start" justify="space-between" wrap="nowrap" gap="sm">
                  <Group align="flex-start" style={{ flex: 1, minWidth: 0 }} wrap="nowrap" gap="sm">
                    <Center
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: themeAlpha(0.1),
                        color: themeVar('accent-text'),
                        flexShrink: 0,
                      }}
                    >
                      {getNotificationIcon(notification.notification_type, 16)}
                    </Center>

                    <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                      <Group justify="space-between" wrap="nowrap" gap={6}>
                        <ThemedText
                          fw={notification.read ? 400 : 600}
                          size="sm"
                          style={{ wordBreak: 'break-word', lineHeight: 1.3 }}
                        >
                          {notification.title}
                        </ThemedText>
                        {!notification.read && (
                          <Box
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: themeVar('accent'),
                              flexShrink: 0,
                              marginTop: 6,
                            }}
                          />
                        )}
                      </Group>

                      {notification.message && (
                        <ThemedText
                          component="div"
                          lineClamp={2}
                          style={{
                            fontSize: 11,
                            color: COLORS.textDescription,
                            wordBreak: 'break-word',
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.message}
                        </ThemedText>
                      )}

                      <ThemedText style={{ fontSize: 10.5, color: COLORS.textSubtle, lineHeight: 1.3 }}>
                        {notification.formatted_time}
                      </ThemedText>
                    </Stack>
                  </Group>

                  {/* Single dismiss action — click on the item body handles mark-as-read */}
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="gray"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(notification.id);
                    }}
                    title="Dismiss"
                    style={{ flexShrink: 0 }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Box>
              <Divider color="#2a2a2a" />
            </Box>
          ))
        )}
      </ScrollArea.Autosize>

      {/* Footer */}
      {notifications && notifications.length > 0 && (
        <Box p="sm" style={{ borderTop: '1px solid #333' }}>
          <Center>
            <a
              href="/notifications"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: themeVar('accent-text'),
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              View all notifications
              <CountBadge count={notifications.length} tone="attention" />
            </a>
          </Center>
        </Box>
      )}
    </Paper>
  );
};

export default NotificationDropdown;
